/*
 * How much of the period a form asks about the client's address or
 * employment history covers, and where the gaps are. Pure (dates in, numbers
 * out), so it is unit-tested directly.
 */
import dayjs, { type Dayjs } from "dayjs";

export type HistoryKind = "address" | "employment";

export type Period = {
  start?: string | null;
  end?: string | null;
  current?: boolean;
};

export type CoverageTarget = {
  start: Dayjs;
  /** "5 years", "Since age 16". */
  span: string;
  /** The form that sets it ("I-485"), or null for the default. */
  form: string | null;
};

export type Gap = { start: Dayjs; end: Dayjs; days: number };

export type Coverage = {
  target: CoverageTarget;
  coveredDays: number;
  totalDays: number;
  /** 0..1 */
  ratio: number;
  gaps: Gap[];
  /** "4 years 2 months". */
  coveredLabel: string;
};

const DEFAULT_YEARS = 5;

const CLOSED_CASE = /closed|withdrawn|denied|approved|complete/i;

/** Open cases set the coverage target and readiness checks. */
export const isOpenCaseStatus = (status: unknown): boolean =>
  !CLOSED_CASE.test(String(status ?? ""));

/** Forms that ask for a history, and how far back. */
const FORMS: Array<{
  form: string;
  match: RegExp;
  address: number | "since16";
  employment: number;
}> = [
  { form: "N-400", match: /n-?400|naturali[sz]/i, address: 5, employment: 5 },
  {
    form: "I-485",
    match: /\baos\b|adjust|i-?485/i,
    address: 5,
    employment: 5
  },
  {
    form: "DS-260",
    match: /ds-?260|consular|immigrant visa|\bk-?1\b|nvc/i,
    address: "since16",
    employment: 5
  }
];

/** The window to cover, set by the most demanding open case. */
export const coverageTarget = (
  kind: HistoryKind,
  caseTypes: string[],
  birthday?: string | null,
  now: Dayjs = dayjs()
): CoverageTarget => {
  let best: CoverageTarget = {
    start: now.subtract(DEFAULT_YEARS, "year"),
    span: `${DEFAULT_YEARS} years`,
    form: null
  };
  const forms = FORMS.filter((f) => caseTypes.some((t) => f.match.test(t)));
  for (const f of forms) {
    const rule = kind === "address" ? f.address : f.employment;
    const born = birthday ? dayjs(birthday) : null;
    const candidate: CoverageTarget | null =
      rule === "since16"
        ? born?.isValid()
          ? { start: born.add(16, "year"), span: "Since age 16", form: f.form }
          : null
        : {
            start: now.subtract(rule, "year"),
            span: `${rule} years`,
            form: f.form
          };
    if (
      candidate &&
      (best.form === null || candidate.start.isBefore(best.start))
    ) {
      best = candidate;
    }
  }
  if (best.start.isAfter(now)) best = { ...best, start: now };
  return best;
};

const spanLabel = (days: number): string => {
  const months = Math.round(days / 30.44);
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [
    y ? `${y} year${y === 1 ? "" : "s"}` : "",
    m ? `${m} month${m === 1 ? "" : "s"}` : ""
  ].filter(Boolean);
  return parts.join(" ") || (days > 0 ? "under a month" : "nothing");
};

/** Gaps shorter than this are rounding between one row's end and the next's start. */
const SLACK_DAYS = 31;

export const computeCoverage = (
  periods: Period[],
  target: CoverageTarget,
  now: Dayjs = dayjs()
): Coverage => {
  const from = target.start.startOf("day");
  const to = now.startOf("day");
  const spans = periods
    .map((p) => {
      const s = p.start ? dayjs(p.start) : null;
      const e = p.current || !p.end ? to : dayjs(p.end);
      return s?.isValid() && e.isValid() ? { s, e } : null;
    })
    .filter((x): x is { s: Dayjs; e: Dayjs } => x !== null)
    .map(({ s, e }) => ({
      s: s.isBefore(from) ? from : s.startOf("day"),
      e: e.isAfter(to) ? to : e.startOf("day")
    }))
    .filter(({ s, e }) => !e.isBefore(s))
    .sort((a, b) => a.s.valueOf() - b.s.valueOf());

  const merged: Array<{ s: Dayjs; e: Dayjs }> = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span.s.diff(last.e, "day") <= SLACK_DAYS) {
      if (span.e.isAfter(last.e)) last.e = span.e;
    } else {
      merged.push({ ...span });
    }
  }

  const gaps: Gap[] = [];
  let cursor = from;
  for (const m of merged) {
    const days = m.s.diff(cursor, "day");
    if (days > SLACK_DAYS) gaps.push({ start: cursor, end: m.s, days });
    if (m.e.isAfter(cursor)) cursor = m.e;
  }
  const tail = to.diff(cursor, "day");
  if (tail > SLACK_DAYS) gaps.push({ start: cursor, end: to, days: tail });

  const totalDays = Math.max(1, to.diff(from, "day"));
  const gapDays = gaps.reduce((n, g) => n + g.days, 0);
  const coveredDays = Math.max(0, totalDays - gapDays);
  return {
    target,
    coveredDays,
    totalDays,
    ratio: Math.min(1, coveredDays / totalDays),
    gaps,
    coveredLabel: spanLabel(coveredDays)
  };
};

/** "Mar 2021 – Aug 2021". */
export const gapLabel = (g: Gap): string =>
  `${g.start.format("MMM YYYY")} – ${g.end.format("MMM YYYY")}`;

/** Rows that end before the window starts stay listed but faded. */
export const isBeforeWindow = (p: Period, target: CoverageTarget): boolean =>
  !p.current && Boolean(p.end) && dayjs(p.end).isBefore(target.start);

/** Employment rows that explain a gap (between jobs, studying). */
export const EXPLAINED_GAP =
  /unemploy|between jobs|job search|student|studying|study|career break|parental leave|caregiv/i;
